import {
  onCleanup,
  createEffect,
  createMemo,
  on,
  onMount,
  createResource,
  createSignal,
  type Accessor,
} from "solid-js";
import {
  customShadowlessElement,
  type CorrectComponentType,
  correctElementType,
} from "@brytdesigns/web-component-core/utils";
import unique from "lodash.uniq";

function formDataToObject(formData: FormData) {
  const obj: Record<string, any> = {};
  for (const [key, value] of formData.entries()) {
    // Check if the key already exists
    if (obj.hasOwnProperty(key)) {
      // If it's not an array, convert it to an array
      if (!Array.isArray(obj[key])) {
        obj[key] = [obj[key]];
      }
      // Push the new value to the array
      obj[key].push(value);
    } else {
      // Assign the value directly
      obj[key] = value;
    }
  }
  return obj;
}

const createFilters = (
  element: HTMLElement,
  formSelector: Accessor<string>,
) => {
  const historyFilters = parseFiltersFromState(history.state?.filters);
  const formFilters = createMemo(() => {
    const form = element.querySelector<HTMLFormElement>(formSelector());
    if (!form) return {};
    const formData = new FormData(form);
    return formDataToObject(formData);
  });

  const mergedFilters = createMemo(() => ({
    ...historyFilters,
    ...formFilters(),
  }));

  const [filters, setFilters] = createSignal(mergedFilters());

  onMount(() => {
    function handlePopState(event: PopStateEvent) {
      if (event.state?.filters === JSON.stringify(filters())) return;
      setFilters(parseFiltersFromState(event.state?.filters));
    }
    addEventListener("popstate", handlePopState);

    return onCleanup(() => {
      removeEventListener("popstate", handlePopState);
    });
  });

  createEffect(() => {
    on(mergedFilters, (mergedFilters) => {
      setFilters((filters) => ({ ...filters, ...mergedFilters }));
    });
  });

  return [filters, setFilters] as const;
};
function parseFiltersFromState(state: string) {
  return state ? JSON.parse(decodeURIComponent(state)) : {};
}

const keywordHandles: Record<string, any> = {
  collection: (url: URL, value = "all") => {
    const parts = url.pathname.split("/");
    parts[2] = value;
    url.pathname = parts.join("/");
    return url;
  },
  tags: (url: URL, value: any) => {
    const parts = url.pathname.split("/");
    if (Array.isArray(value)) value = value.join("+");
    parts[3] = value;
    if (!value?.length) parts.pop();
    url.pathname = parts.join("/");
    return url;
  },
};

function getFormSelector(formId: Accessor<string | null> = () => null) {
  return formId ? `#${formId()}` : "form";
}

const ignoredKeys = ["view"];

function getNewUrl(filters: Record<string, any>) {
  const url = new URL(window.location.href);
  const filterKeys = Object.keys(filters);

  Array.from(url.searchParams.keys()).forEach((key) => {
    if (ignoredKeys.includes(key)) return;
    if (!filterKeys.includes(key)) url.searchParams.delete(key);
    else if (filterKeys.includes(key) && !filters[key].length)
      url.searchParams.delete(key);
  });

  filterKeys.forEach((key) => {
    let value = filters[key];
    if (keywordHandles[key]) keywordHandles[key](url, value);
    else {
      const isArray = Array.isArray(value);
      if (!value || (isArray && !value.length)) return;
      if (isArray) {
        url.searchParams.delete(key);

        value.forEach((val) => {
          url.searchParams.append(key, val);
        });
      } else {
        url.searchParams.set(key, value);
      }
    }
  });

  return url;
}

type Props = {
  productsSelector: string;
  paginationSelector: string;
  formId: string;
  sectionId: string;
  listItemKey: string;
};

const CollectionFiltersHandler: CorrectComponentType<Props> = (
  props,
  { element },
) => {
  const domParser = new DOMParser();

  const [filters, setFilters] = createFilters(element, () =>
    getFormSelector(() => props.formId),
  );

  let controller: AbortController | null = null;

  const [htmlText] = createResource(filters, async (filters) => {
    const url = getNewUrl(filters);
    url.searchParams.set("section_id", props.sectionId);

    if (controller) controller.abort("Received another request");

    controller = new AbortController();

    try {
      const htmlText = await fetch(url, { signal: controller.signal }).then(
        (res) => res.text(),
      );
      const jsonFilters = JSON.stringify(filters);
      url.searchParams.delete("section_id");
      window.history.replaceState({ filters: jsonFilters }, "", url);
      return htmlText;
    } catch (err) {
      console.warn(err);
      return "";
    }
  });

  createEffect(
    on(
      () => ({
        paginationSelector: props.paginationSelector,
        htmlText: htmlText(),
      }),
      ({ paginationSelector, htmlText }) => {
        if (!paginationSelector || !htmlText) return;

        const paginationTarget = element.querySelector(paginationSelector);
        if (!paginationTarget) return;

        const html = domParser.parseFromString(htmlText, "text/html");
        const newListPagination = html.querySelector(paginationSelector);
        if (!newListPagination) return;

        paginationTarget.replaceChildren(
          ...Array.from(newListPagination.children),
        );
      },
    ),
  );

  createEffect(
    on(
      () => ({
        productsSelector: props.productsSelector,
        listItemKey: props.listItemKey,
        htmlText: htmlText(),
      }),
      ({ productsSelector, listItemKey, htmlText }) => {
        if (!productsSelector || !htmlText) return;

        const productsTarget = element.querySelector(productsSelector);
        if (!productsTarget) return;

        const html = domParser.parseFromString(htmlText, "text/html");
        const newProducts = html.querySelector(productsSelector);
        if (!newProducts) return;

        const oldChildren = Array.from(productsTarget.children);
        const newChildren = Array.from(newProducts.children);
        const mergedChildren = newChildren.map((child) => {
          const existing = oldChildren.find(
            (oldChild) =>
              child.hasAttribute(listItemKey) &&
              oldChild.hasAttribute(listItemKey) &&
              oldChild.getAttribute(listItemKey) ===
              child.getAttribute(listItemKey),
          );
          if (existing) return existing;
          return child;
        });
        productsTarget.replaceChildren(...mergedChildren);
      },
    ),
  );

  createEffect(
    on(
      () => ({ paginationSelector: props.paginationSelector }),
      ({ paginationSelector }) => {
        if (!paginationSelector) return;
        const paginationTarget =
          element.querySelector<HTMLAnchorElement>(paginationSelector);
        if (!paginationTarget) return;

        function handleClick(e: MouseEvent) {
          e.preventDefault();
          const eventTarget = e.target as HTMLAnchorElement;
          try {
            let target: HTMLAnchorElement | null = null;
            if (eventTarget.tagName === "A") target = eventTarget;
            else target = eventTarget.closest("a");
            if (!target) return;
            const url = new URL(target.href);
            let newPage = url.searchParams.get("page");
            setFilters((filters) => ({ ...filters, page: newPage }));
          } catch (error) { }
        }
        paginationTarget.addEventListener("click", handleClick);
        return onCleanup(() => {
          paginationTarget.removeEventListener("click", handleClick);
        });
      },
    ),
  );

  onMount(() => {
    function handleChange(e: Event) {
      const eventTarget = e.target as HTMLInputElement;
      const type = eventTarget.type;
      switch (type) {
        case "checkbox":
          return updateCheckboxFilter(eventTarget);
        default:
          return setFilters((filters) => {
            const pagination: Record<string, any> = {};
            if (filters.page) pagination.page = "1";

            return {
              ...filters,
              [eventTarget.name]: eventTarget.value,
              ...pagination,
            };
          });
      }
    }
    function handleReset(e: Event) {
      e.preventDefault();
      setTimeout(() => {
        const form = e.target as HTMLFormElement;
        const formObject = formDataToObject(new FormData(form));
        const url = new URL(window.location.href);
        url.search = "";
        setFilters({ tags: [], ...formObject });
        window.location.href = url.toString();
      });
    }

    function handleSubmit(e: SubmitEvent) {
      e.preventDefault();
      const formObject = formDataToObject(
        new FormData(e.target as HTMLFormElement),
      );
      setFilters((filters) => ({ ...filters, ...formObject }));
    }

    element.addEventListener("input", handleChange);
    element.addEventListener("reset", handleReset);
    element.addEventListener("submit", handleSubmit);

    return onCleanup(() => {
      element.removeEventListener("input", handleChange);
      element.removeEventListener("reset", handleReset);
      element.removeEventListener("submit", handleSubmit);
    });
  });

  function updateCheckboxFilter(checkbox: HTMLInputElement) {
    if (checkbox.checked) {
      return setFilters((filters) => {
        const pagination: Record<string, any> = {};
        if (filters.page) pagination.page = "1";

        const currentValue = filters[checkbox.name];
        if (!currentValue)
          return {
            ...filters,
            [checkbox.name]: [checkbox.value],
            ...pagination,
          };
        if (!Array.isArray(currentValue))
          return {
            ...filters,
            [checkbox.name]: [currentValue, checkbox.value],
            ...pagination,
          };
        return {
          ...filters,
          [checkbox.name]: unique([...filters[checkbox.name], checkbox.value]),
          ...pagination,
        };
      });
    }

    return setFilters((filters) => {
      const pagination: Record<string, any> = {};
      if (filters.page) pagination.page = "1";

      const currentValue = filters[checkbox.name];
      if (!Array.isArray(currentValue))
        return { ...filters, [checkbox.name]: [], ...pagination };
      return {
        ...filters,
        [checkbox.name]:
          filters[checkbox.name]?.filter((v: string) => v !== checkbox.value) ||
          [],
        ...pagination,
      };
    });
  }
};

customShadowlessElement(
  "collection-filters-handler",
  {
    productsSelector: "",
    paginationSelector: "",
    formId: "",
    sectionId: "",
    listItemKey: "id",
  },
  correctElementType(CollectionFiltersHandler),
);
